import { BlockedSlot } from '@/types/approvals'

export type BlockedSlotCopy = {
  title: string
  fix?: string
}

const KNOWN_REASONS: Record<string, BlockedSlotCopy> = {
  unsupported_scope: {
    title: 'This position is set up in a way pixydocs cannot resolve yet.',
    fix: 'Contact support to review this position.',
  },
  scope_not_found: {
    title: 'The job this position looks up on the document could not be found.',
    fix: 'Check that the document is linked to the right project or job.',
  },
  scope_has_no_department: {
    title: 'The job this position looks up has no department set.',
    fix: 'Set a department on the job in the ERP.',
  },
  unknown_field: {
    title: "The document field this position reads from doesn't exist.",
    fix: 'Reconfigure this position with a valid field.',
  },
  no_holder: {
    title: 'Nobody currently holds this position.',
    fix: 'Assign someone to this position in the ERP.',
  },
  ambiguous_reference: {
    title: 'The ERP returned more than one answer for this position.',
    fix: 'Fix the duplicate or conflicting record in the ERP.',
  },
  no_starting_point_value: {
    title: 'The document has no value for the field this position reads from.',
    fix: 'Fill in that field on the document.',
  },
  holder_not_a_user: {
    title:
      "The person who holds this position doesn't have a Pixydocs account.",
    fix: 'Invite them to Pixydocs, or update who holds the position in the ERP.',
  },
  holder_not_found: {
    title:
      "The person who holds this position isn't in the ERP records synced to Pixydocs.",
    fix: 'Check that the ERP sync has run and includes this person, then try again.',
  },
  ambiguous_user_match: {
    title:
      'Two Pixydocs users share the email address of the person who holds this position.',
    fix: 'Ask an admin to make each user’s email unique.',
  },
  position_version_unknown: {
    title: 'This position has no known version to resolve holders from.',
    fix: 'Contact support to review this position.',
  },
  position_source_not_configured: {
    title:
      "This organization hasn't set up a connection to look up this position.",
    fix: 'Ask an admin to set up the ERP credential for this organization.',
  },
  position_lookup_rejected: {
    title: 'The ERP refused this lookup.',
    fix: "Ask an admin to check this position's configuration.",
  },
  position_source_unavailable: {
    title: 'The ERP could not be reached to look up this position.',
    fix: 'Try again shortly.',
  },
  project_not_found: {
    title:
      "The project this position's ERP connection comes from could not be found.",
    fix: 'Check that the document is linked to the right project.',
  },
  position_answer_unreadable: {
    title: "The ERP's answer for this position could not be read.",
    fix: 'Contact support — this needs an engineer to look into.',
  },
}

/**
 * Turns a blocked slot's reason into text people can read. The set of
 * possible reasons is open-ended, so a reason this app doesn't recognize yet
 * falls back to the server's own message with no fix hint, instead of being
 * treated as an error.
 */
export function blockedSlotCopy(blocked: BlockedSlot): BlockedSlotCopy {
  return KNOWN_REASONS[blocked.reason] ?? { title: blocked.message }
}
